import {
  Button,
  CodeEditor,
  Input,
  InputErrorMessage,
  Select,
} from "@cynthai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import { useCurrentOrganization } from "@/core/hooks/use-current-organization";
import { useTheme } from "@/core/hooks/use-theme";
import {
  type ApiErrorLike,
  apiErrorData,
  isApiError,
} from "@/core/lib/api-errors";
import { ledgerQueryReasonMessage } from "@/features/classification/lib/ledger-error-message";
import {
  sourceQuerySchema,
  type SourceQueryValues,
  toLedgerQuery,
} from "@/features/classification/lib/ledger-source-query";
import type {
  LedgerClassificationTemplateQuery,
  UpdateLedgerTemplateQueryError,
} from "@/openapi";
import {
  listOriginsOptions,
  readLedgerTemplateQueryOptions,
  updateLedgerTemplateQueryMutation,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

export function LedgerSourceQueryEditor({
  templateId,
}: Readonly<{ templateId: string }>) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const query = useQuery(
    readLedgerTemplateQueryOptions({
      path: { org_id: organizationId, template_id: templateId },
    }),
  );
  if (query.isPending) return <p>{m.loading_spinner()}</p>;
  if (query.isError)
    return <InputErrorMessage>{m.error_occurred()}</InputErrorMessage>;
  return (
    <SourceQueryForm
      key={templateId}
      templateId={templateId}
      initial={query.data}
    />
  );
}

function SourceQueryForm({
  templateId,
  initial,
}: Readonly<{
  templateId: string;
  initial: LedgerClassificationTemplateQuery;
}>) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const client = useQueryClient();
  const form = useForm<SourceQueryValues>({
    resolver: zodResolver(sourceQuerySchema),
    mode: "onChange",
    defaultValues: {
      origin_id: initial.origin_id,
      sql_query: initial.sql_query,
      identifiers: initial.row_identifier_columns.join(", "),
      columns: initial.column_order.join(", "),
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid, errors },
  } = form;
  const save = useMutation({
    ...updateLedgerTemplateQueryMutation(),
    onSuccess: (data) => {
      client.setQueryData(
        readLedgerTemplateQueryOptions({
          path: { org_id: organizationId, template_id: templateId },
        }).queryKey,
        data,
      );
      reset({
        origin_id: data.origin_id,
        sql_query: data.sql_query,
        identifiers: data.row_identifier_columns.join(", "),
        columns: data.column_order.join(", "),
      });
    },
  });
  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) =>
          save.mutate({
            path: { org_id: organizationId, template_id: templateId },
            body: toLedgerQuery(values),
          }),
        )}
      >
        <p className="text-text-secondary">{m.ledger_query_description()}</p>
        <LedgerSourceQueryFields isDisabled={save.isPending} />
        {save.isError && !errors.columns && (
          <InputErrorMessage>{queryErrorMessage(save.error)}</InputErrorMessage>
        )}
        {save.isSuccess && !isDirty && (
          <output className="text-text-primary">
            {m.ledger_query_saved()}
          </output>
        )}
        <Button
          type="submit"
          color="primary"
          className="self-start"
          isDisabled={!isDirty || !isValid || save.isPending}
          isLoading={save.isPending}
        >
          {m.ledger_query_save()}
        </Button>
      </form>
    </FormProvider>
  );
}

/** Source query inputs for any form whose values include `SourceQueryValues`. */
export function LedgerSourceQueryFields({
  isDisabled,
}: Readonly<{ isDisabled: boolean }>) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const theme = useTheme();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<SourceQueryValues>();
  const sources = useQuery(
    listOriginsOptions({
      path: { org_id: organizationId },
    }),
  );
  return (
    <>
      <Controller
        control={control}
        name="origin_id"
        render={({ field, fieldState }) => (
          <Select
            label={m.ledger_query_origin()}
            value={field.value}
            onChange={field.onChange}
            errorMessage={fieldState.error?.message}
            items={(sources.data ?? [])
              .filter(
                (source) =>
                  source.config.type === "ms_sql" ||
                  source.config.type === "pg_view",
              )
              .map((source) => ({
                value: source.id,
                label: source.name,
              }))}
            isDisabled={sources.isPending || isDisabled}
          />
        )}
      />
      {sources.isError && (
        <InputErrorMessage>{m.error_occurred()}</InputErrorMessage>
      )}
      <Controller
        control={control}
        name="sql_query"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-2">
            <CodeEditor
              label={m.classification_template_sql_label()}
              className="h-128"
              language="sql"
              theme={theme}
              value={field.value}
              onChange={field.onChange}
            />
            {fieldState.error && (
              <InputErrorMessage>{fieldState.error.message}</InputErrorMessage>
            )}
          </div>
        )}
      />
      <Input
        label={m.classification_template_row_identifiers_label()}
        isInvalid={!!errors.identifiers}
        errorMessage={errors.identifiers?.message}
        {...register("identifiers")}
      />
      <Input
        label={m.ledger_query_column_order()}
        isInvalid={!!errors.columns}
        errorMessage={errors.columns?.message}
        {...register("columns")}
      />
    </>
  );
}

function queryErrorMessage(
  error: ApiErrorLike<UpdateLedgerTemplateQueryError>,
) {
  if (isApiError(error, "CLASSIFICATION_IDENTIFIER_COLUMNS_FROZEN")) {
    return m.ledger_query_identifiers_frozen();
  }
  if (isApiError(error, "INVALID_LEDGER_QUERY")) {
    const reason = apiErrorData(error)?.reason;
    if (reason) return ledgerQueryReasonMessage(reason);
  }
  return m.ledger_query_save_failed();
}
